import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch } from '../../src/redux/store';
import {
  confirmSafety,
  selectOnboardingAnswers,
  selectOnboardingCompleted,
  selectOnboardingSafety,
} from '../../src/redux/onboardingSlice';
import { setAllergens, setTasteAxes, selectTasteAxes } from '../../src/redux/userPreferencesSlice';
import { ALLERGEN_KEYS, type AllergenKey } from '../../src/data/allergens';
import { profileFromAnswers } from '../../src/services/tasteAxes';
import { OnboardingFrame, ONBOARDING_DESKTOP_MIN } from '../../src/components/onboarding/OnboardingFrame';
import { DuChefBubble } from '../../src/components/onboarding/DuChefBubble';
import { AllergenChip } from '../../src/components/onboarding/AllergenChip';
import { Paprika } from '../../src/styles/theme';

/**
 * Route /onboarding/safety — allergies before any proposal. Confirming stores
 * the allergens, derives the taste axes from the three duels, and hands over
 * to the profile screen, which reads both back and builds the first week.
 */
export default function OnboardingSafety() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const desktop = width >= ONBOARDING_DESKTOP_MIN;

  const completed = useSelector(selectOnboardingCompleted);
  const answers = useSelector(selectOnboardingAnswers);
  const saved = useSelector(selectOnboardingSafety);
  const axesSoFar = useSelector(selectTasteAxes);

  const [keys, setKeys] = useState<AllergenKey[]>(saved?.keys ?? []);
  const [custom, setCustom] = useState<string[]>(saved?.custom ?? []);
  const [none, setNone] = useState<boolean>(saved?.none ?? false);
  const [text, setText] = useState('');

  // Only the focused screen may redirect (see the quiz screen).
  const focused = useIsFocused();
  useEffect(() => {
    if (!focused) return;
    if (completed) router.replace('/(app)');
    else if (answers.length < 3) router.replace('/onboarding');
  }, [focused, completed, answers.length, router]);

  const toggleKey = (k: AllergenKey) => {
    setNone(false);
    setKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  };
  const removeCustom = (c: string) => setCustom((prev) => prev.filter((x) => x !== c));
  const addCustom = () => {
    const v = text.trim();
    if (!v) return;
    const cap = v.charAt(0).toUpperCase() + v.slice(1);
    setNone(false);
    setCustom((prev) => (prev.includes(cap) ? prev : [...prev, cap]));
    setText('');
  };
  const toggleNone = () => {
    setNone((prev) => {
      if (!prev) { setKeys([]); setCustom([]); }
      return !prev;
    });
  };

  const ready = none || keys.length > 0 || custom.length > 0;

  const confirm = useCallback(() => {
    if (!ready) return;
    const axes = profileFromAnswers(answers);
    if (!axes) { router.replace('/onboarding'); return; }
    const chosen = none ? { keys: [] as AllergenKey[], custom: [] as string[] } : { keys, custom };
    dispatch(setAllergens(chosen));
    dispatch(confirmSafety({ ...chosen, none }));
    // Derive the axes once. Coming back here from the profile screen's
    // « Modifier » must not undo a flip the cook already made there; a new
    // answer to a duel clears `saved`, so the axes are re-derived then.
    if (!saved || !axesSoFar) dispatch(setTasteAxes(axes));
    // The profile opens on the beat that reads these answers back.
    router.push({ pathname: '/onboarding/profile', params: { beat: '1' } });
  }, [ready, answers, none, keys, custom, saved, axesSoFar, dispatch, router]);

  const bubble = <DuChefBubble tone="grave" testID="onb-safety-bubble">{t('onboarding.safety.prompt')}</DuChefBubble>;
  const confirmButton = (
    <Pressable
      onPress={confirm}
      disabled={!ready}
      testID="onb-confirm"
      accessibilityRole="button"
      accessibilityState={{ disabled: !ready }}
      style={[styles.confirm, !ready && styles.confirmDisabled]}
    >
      <Text style={styles.confirmText}>{t('onboarding.safety.confirm')}</Text>
    </Pressable>
  );

  // Desktop (S2, decision 2026-09-17): the form is a focused column in the
  // middle of the pane, the question and the promise above it, Confirmer
  // right under it. The phone keeps the bubble on top and the footer button.
  const body = (
    <View style={[styles.body, desktop && styles.bodyDesktop]}>
      {desktop && (
        <>
          <Text style={styles.deskTitle}>{t('onboarding.safety.prompt')}</Text>
          <Text style={styles.deskPromise}>{t('onboarding.safety.promise')}</Text>
        </>
      )}
      <Text style={styles.kicker}>{t('onboarding.safety.sectionKicker')}</Text>
      <View style={styles.chips}>
        {ALLERGEN_KEYS.map((k) => (
          <AllergenChip key={k} testID={`onb-allergen-${k}`} label={t(`onboarding.safety.allergen.${k}`)} selected={keys.includes(k)} onPress={() => toggleKey(k)} />
        ))}
        {custom.map((c) => (
          <AllergenChip key={`custom-${c}`} testID={`onb-allergen-custom-${c.toLowerCase()}`} label={c} selected onPress={() => removeCustom(c)} />
        ))}
      </View>
      <View style={styles.addRow}>
        <TextInput
          value={text}
          onChangeText={setText}
          onSubmitEditing={addCustom}
          placeholder={t('onboarding.safety.otherPlaceholder')}
          placeholderTextColor={Paprika.textMuted}
          style={styles.input}
          testID="onb-other-input"
          returnKeyType="done"
        />
        <Pressable onPress={addCustom} disabled={!text.trim()} testID="onb-other-add" accessibilityRole="button" style={[styles.addBtn, !text.trim() && styles.addBtnDisabled]}>
          <Text style={styles.addBtnText}>{t('onboarding.safety.add')}</Text>
        </Pressable>
      </View>
      <Pressable onPress={toggleNone} testID="onb-none" accessibilityRole="checkbox" accessibilityState={{ checked: none }} style={[styles.nothing, none && styles.nothingOn]}>
        <View style={[styles.check, none && styles.checkOn]}>{none && <Feather name="check" size={12} color="#ffffff" />}</View>
        <Text style={[styles.nothingText, none && styles.nothingTextOn]}>{t('onboarding.safety.nothing')}</Text>
      </Pressable>
      {desktop && <View style={styles.deskConfirm}>{confirmButton}</View>}
    </View>
  );

  return (
    <View style={styles.root}>
      <OnboardingFrame
        kicker={t('onboarding.safety.kicker')}
        rail={bubble}
        footer={confirmButton}
        railFooter={desktop ? <View /> : undefined}
        scroll
      >
        {desktop ? <View style={styles.deskCenter}>{body}</View> : body}
      </OnboardingFrame>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Paprika.background },
  body: { gap: 14 },
  deskCenter: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  bodyDesktop: { width: '100%', maxWidth: 600, gap: 16 },
  deskTitle: { fontFamily: 'Newsreader-Regular', fontSize: 30, lineHeight: 36, color: Paprika.textPrimary },
  deskPromise: { fontFamily: 'Inter-Regular', fontSize: 14, lineHeight: 21, color: Paprika.textSecondary, marginBottom: 4 },
  deskConfirm: { marginTop: 8 },
  kicker: { fontFamily: 'Inter-SemiBold', fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: Paprika.textMuted },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  addRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  input: {
    flex: 1, height: 44, paddingHorizontal: 14, borderRadius: 999, backgroundColor: Paprika.cardBg,
    borderWidth: 1, borderColor: Paprika.hairline, fontFamily: 'Inter-Regular', fontSize: 14, color: Paprika.textPrimary,
  },
  addBtn: { height: 44, paddingHorizontal: 16, borderRadius: 999, backgroundColor: Paprika.bgSunk, alignItems: 'center', justifyContent: 'center' },
  addBtnDisabled: { opacity: 0.45 },
  addBtnText: { fontFamily: 'Inter-SemiBold', fontSize: 13, color: Paprika.textPrimary },
  nothing: {
    flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14,
    backgroundColor: Paprika.cardBg, borderWidth: 1, borderColor: Paprika.hairline,
  },
  nothingOn: { backgroundColor: Paprika.brandTint, borderColor: Paprika.brandSoft },
  check: { width: 22, height: 22, borderRadius: 11, borderWidth: 1.5, borderColor: Paprika.border, alignItems: 'center', justifyContent: 'center' },
  checkOn: { backgroundColor: Paprika.accent, borderColor: Paprika.accent },
  nothingText: { fontFamily: 'Inter-Medium', fontSize: 14, color: Paprika.textPrimary },
  nothingTextOn: { color: Paprika.accent },
  confirm: { height: 50, borderRadius: 14, backgroundColor: Paprika.accent, alignItems: 'center', justifyContent: 'center' },
  confirmDisabled: { opacity: 0.45 },
  confirmText: { fontFamily: 'Inter-SemiBold', fontSize: 15, color: '#ffffff' },
});
